import React, { useEffect, useState, useCallback } from 'react';
import { useUnityBuildStore } from '../../../stores/store';
import { useStore } from 'zustand';
import { FormulaHeader } from './FormulaHeader';

import { CreateAssemblyProps, Foldertype } from '../../../types/FolderTypes';
import {
  useCreateGlobalAssemblyMutation,
  useUpdateGlobalAssemblyMutation,
} from '../../../hooks/useFetchAssembliesByFileId';
import { useSubmitTakeoffAssembly } from '../../../hooks/useSubmitTakeoffAssembly';

import { useGlobalItemStore } from '../../../stores/globalItemStore';
import { defaultGlobalAssembly, useAssemblyStore } from '../../../stores/AssemblyStore';
import { useFolderStore } from '../../../stores/folderStore';
import { GlobalAssembly } from '../../../api/protosCompiled/globalAssembly/globalAssembly_pb';
import ItemTab from '../items/ItemTab';
import CreateItemTab from '../items/CreateItemTab';
import { AddedItemRow } from '../items/AddedItemRow';
import BreakoutSelector from '../../takeoffPallette/BreakoutSelector';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  CircularProgress,
  Alert,
  Snackbar,
  Box,
  TableContainer,
  DialogTitle,
  DialogActions,
  useTheme as useMuiTheme,
} from '@mui/material';
import { ProjectEstimateAssemblyService as EstimateAssemblyService } from '../../../api/ProjectEstimateAssemblyService';
import { ContractChangeOrderAssemblyService as ChangeOrderAssemblyService } from '../../../api/ContractChangeOrderAssemblyService';
import { useHandleGlobalStateState } from '../../../hooks/useHandleGlobalState';
import { TakeoffAssembly } from '../../../types/AssemblyItemType';
import HelpOutline from '@mui/icons-material/HelpOutline';
import { usePreloadFoundryItems } from '../../../hooks/usePreloadFoundryItems';
import { alpha } from '@mui/material/styles';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAssemblyRowActions } from '../../accessManager';

export const isValidTakeoffVariable = (variable: string, measurementType: string): boolean => {
  switch (measurementType) {
    case 'count':
      return variable === 'point';
    case 'length':
      return ['point', 'segment', 'length'].includes(variable);
    case 'area':
      return true; // All variables are valid for area
    default:
      return true;
  }
};

const validateAssemblyItems = (assembly: any): boolean => {
  if (!assembly || !assembly.itemsList || assembly.itemsList.length === 0) {
    return true;
  }

  const measurementType = assembly.assemblymeasurementtype;
  let invalidItemsFound = false;
  let invalidItems: string[] = [];

  assembly.itemsList.forEach((item: any, index: number) => {
    if (item.itemqtyformula?.takeoffvariabletype) {
      const takeoffVar = item.itemqtyformula.takeoffvariabletype;
      if (!isValidTakeoffVariable(takeoffVar, measurementType)) {
        invalidItemsFound = true;
        invalidItems.push(`Item #${index + 1}: ${item.itemname || 'Unnamed item'}`);
      }
    }
  });

  if (invalidItemsFound) {
    alert(
      `Cannot save assembly with invalid takeoff variables.\n\nThe following items have invalid variables for a ${measurementType} assembly:\n${invalidItems.join('\n')}\n\nPlease fix these issues before saving.`
    );
    return false;
  }

  return true;
};

function CreateAssembly({ assemblyOption, currentAssembly }: CreateAssemblyProps) {
  //zustand states
  const { selectedEstimate, selectedChangeOrder, userScale } = useStore(useUnityBuildStore);

  const {
    isCreatingAssembly,
    isEditMode,
    setIsEditMode,
    setAssemblyOption,
    setIsViewingAssemblyForm,
    setIsCreatingAssembly,
    setCurrentGlobalAssembly,
    setCurrentTakeoffAssembly,
  } = useStore(useAssemblyStore, state => ({
    isCreatingAssembly: state.isCreatingAssembly,
    isEditMode: state.isEditMode,
    setIsEditMode: state.setIsEditMode,
    setAssemblyOption: state.setAssemblyOption,
    setIsViewingAssemblyForm: state.setIsViewingAssemblyForm,
    setIsCreatingAssembly: state.setIsCreatingAssembly,
    setCurrentGlobalAssembly: state.setCurrentGlobalAssembly,
    setCurrentTakeoffAssembly: state.setCurrentTakeoffAssembly,
  }));

  const {
    isViewingItemForm,
    isCreatingItem,
    setIsCreatingItem,
    setIsViewingItemForm,
    setCurrentItem,
  } = useStore(useGlobalItemStore, state => ({
    isViewingItemForm: state.isViewingItemForm,
    isCreatingItem: state.isCreatingItem,
    setIsCreatingItem: state.setIsCreatingItem,
    setIsViewingItemForm: state.setIsViewingItemForm,
    setCurrentItem: state.setCurrentItem,
  }));

  const { handleDisableRowActions } = useAssemblyRowActions();

  const { currentFolder, setCurrentFolder } = useStore(useFolderStore, state => ({
    currentFolder: state.currentFolder,
    setCurrentFolder: state.setCurrentFolder,
  }));

  //react-query states
  const { submitAssembly } = useSubmitTakeoffAssembly();

  // Preload BuildFoundry items when assembly is loaded
  usePreloadFoundryItems();

  //react-query mutations
  const { mutateAsync: createGlobalAssembly } = useCreateGlobalAssemblyMutation();
  const { mutateAsync: updateGlobalAssembly } = useUpdateGlobalAssemblyMutation();

  const [shouldSubmit, setShouldSubmit] = useState(false);
  const [takeoffType, setTakeoffType] = useState('');
  const [lastSaveAttempt, setLastSaveAttempt] = useState<number>(0);

  const handleSetViewingState = useHandleGlobalStateState();

  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const muiTheme = useMuiTheme();
  const { darkMode } = useTheme();

  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState<boolean>(false);
  const [exitAction, setExitAction] = useState<'close' | 'mode-switch' | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState<string>('');

  // Create a snapshot of the current assembly to compare against
  const getAssemblySnapshot = useCallback((assembly: any) => {
    if (!assembly) return '';
    return JSON.stringify({
      name: assembly.assemblyname,
      type: assembly.assemblymeasurementtype,
      classification: assembly.imageclassificationid,
      items: assembly.itemsList,
      itemCount: assembly.itemsList?.length || 0,
    });
  }, []);

  // Check for exponential item growth that might indicate duplication issues
  const detectExponentialGrowth = useCallback((currentItems: any[], previousSnapshot: string) => {
    if (!previousSnapshot) return false;

    try {
      const previous = JSON.parse(previousSnapshot);
      const previousCount = previous.itemCount || 0;
      const currentCount = currentItems?.length || 0;

      // Detect if items have doubled or more (potential exponential growth)
      if (previousCount > 0 && currentCount >= previousCount * 2) {
        console.error('Exponential item growth detected!', {
          previous: previousCount,
          current: currentCount,
          ratio: currentCount / previousCount,
        });
        return true;
      }
    } catch (error) {
      console.warn('Error parsing previous snapshot for growth detection:', error);
    }

    return false;
  }, []);

  // When a new assembly is loaded or after save, store its initial state
  useEffect(() => {
    if (currentAssembly) {
      const snapshot = getAssemblySnapshot(currentAssembly);
      setInitialSnapshot(snapshot);
      setHasUnsavedChanges(false);
    }
  }, [currentAssembly, getAssemblySnapshot]);

  // Check for changes whenever relevant assembly properties change
  useEffect(() => {
    if (currentAssembly && isEditMode) {
      // Only check for changes if we already have an initial snapshot
      // This prevents marking as changed when first entering edit mode
      if (initialSnapshot) {
        const currentSnapshot = getAssemblySnapshot(currentAssembly);
        setHasUnsavedChanges(currentSnapshot !== initialSnapshot);
      }
    }
  }, [
    currentAssembly,
    currentAssembly?.assemblyname,
    currentAssembly?.assemblymeasurementtype,
    currentAssembly?.imageclassificationid,
    currentAssembly?.itemsList,
    initialSnapshot,
    isEditMode,
    getAssemblySnapshot,
  ]);

  // Reset unsaved changes state when exiting edit mode
  useEffect(() => {
    if (!isEditMode) {
      setHasUnsavedChanges(false);
    }
  }, [isEditMode]);

  // Handle successful save
  const handleSuccessfulSave = useCallback(() => {
    if (currentAssembly) {
      const newSnapshot = getAssemblySnapshot(currentAssembly);
      setInitialSnapshot(newSnapshot);
      setHasUnsavedChanges(false);
    }
  }, [currentAssembly, getAssemblySnapshot]);

  // Make sure we have the handleSetAssemblyName function defined before we use it
  const handleSetAssemblyName = useCallback(
    (assemblyname: string) => {
      // setAssemblyName(assemblyname);
      if (currentAssembly && assemblyOption === 'global') {
        setCurrentGlobalAssembly(prev =>
          prev
            ? {
                ...prev,
                assemblyname,
              }
            : null
        );
      } else if (currentAssembly && assemblyOption === 'takeoff') {
        setCurrentTakeoffAssembly(prev =>
          prev
            ? {
                ...prev,
                assemblyname,
              }
            : null
        );
      }
    },
    [currentAssembly, assemblyOption, setCurrentGlobalAssembly, setCurrentTakeoffAssembly]
  );

  const [localAssemblyName, setLocalAssemblyName] = useState('');

  // effect to set the form to the current item
  useEffect(() => {
    if (currentAssembly) {
      // setAssemblyName(currentAssembly?.assemblyname);
      setLocalAssemblyName(currentAssembly?.assemblyname);
      setTakeoffType(currentAssembly?.assemblymeasurementtype || 'count');
    } else {
      //set to a default global assembly
      setCurrentGlobalAssembly({
        ...defaultGlobalAssembly,
        assemblymeasurementtype: 'count',
      });
      setTakeoffType('count');
    }
  }, [currentAssembly, setCurrentGlobalAssembly]);

  // Memoize the input change handler to prevent re-renders
  const handleLocalNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalAssemblyName(e.target.value);
  }, []);

  // Memoize the blur handler to update the global state only when focus leaves the field
  const handleNameBlur = useCallback(() => {
    handleSetAssemblyName(localAssemblyName);
  }, [localAssemblyName, handleSetAssemblyName]);

  const handleMeasurementTypeChange = useCallback(
    (e: any) => {
      const value = e.target.value;
      setTakeoffType(value);
      if (currentAssembly && assemblyOption === 'global') {
        setCurrentGlobalAssembly(prev =>
          prev
            ? {
                ...prev,
                assemblymeasurementtype: value,
              }
            : null
        );
      } else if (currentAssembly && assemblyOption === 'takeoff') {
        setCurrentTakeoffAssembly(prev =>
          prev
            ? {
                ...prev,
                assemblymeasurementtype: value,
              }
            : null
        );
      }
    },
    [currentAssembly, assemblyOption, setCurrentGlobalAssembly, setCurrentTakeoffAssembly]
  );

  const performModeSwitch = useCallback(() => {
    if (isCreatingAssembly) {
      setIsCreatingAssembly(false);
      setIsCreatingItem(true);
      setIsViewingItemForm(true);
    } else if (!isCreatingAssembly) {
      setIsCreatingAssembly(true);
      setIsCreatingItem(false);
      setIsViewingItemForm(false);
    }
    setIsEditMode(false);
    setCurrentItem(null);
    setCurrentTakeoffAssembly(null);
    setCurrentGlobalAssembly(null);
    setCurrentFolder(null);
    setHasUnsavedChanges(false);
  }, [
    isCreatingAssembly,
    setIsCreatingAssembly,
    setIsCreatingItem,
    setIsViewingItemForm,
    setIsEditMode,
    setCurrentItem,
    setCurrentTakeoffAssembly,
    setCurrentGlobalAssembly,
    setCurrentFolder,
    setHasUnsavedChanges,
  ]);

  /**
   * Handles the form state and what we can do in a given state
   */
  const handleItemOrAssemblyMode = useCallback(() => {
    if (hasUnsavedChanges) {
      setExitAction('mode-switch');
      setShowUnsavedDialog(true);
      return;
    }

    performModeSwitch();
  }, [hasUnsavedChanges, performModeSwitch]);

  const closeForm = useCallback(() => {
    setHasUnsavedChanges(false);
    handleSetViewingState({
      resetOption: 'resetAssembly',
      setStateFunctions: [
        {
          setStateFunction: setIsViewingAssemblyForm,
          prop: false,
        },
        {
          setStateFunction: setIsEditMode,
          prop: false,
        },
      ],
    });
  }, [setIsViewingAssemblyForm, setIsEditMode, handleSetViewingState]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!shouldSubmit) return;

      // Prevent concurrent save operations
      if (isSaving) {
        console.warn('Save already in progress, ignoring duplicate request');
        setShouldSubmit(false);
        return;
      }

      if (currentAssembly?.itemsList.length === 0) {
        alert('Please add items to your assembly');
        setShouldSubmit(false);
        return;
      }

      // Validate takeoff variables before saving
      if (currentAssembly && !validateAssemblyItems(currentAssembly)) {
        setShouldSubmit(false);
        return;
      }

      // Check for exponential growth that might indicate duplication issues
      if (currentAssembly && detectExponentialGrowth(currentAssembly.itemsList, initialSnapshot)) {
        const confirmSave = window.confirm(
          'Warning: Unusual item growth detected in this assembly. This might indicate a duplication issue.\n\n' +
            'Do you want to proceed with saving? If you suspect duplicate items, please review the assembly items before continuing.'
        );

        if (!confirmSave) {
          setShouldSubmit(false);
          return;
        }
      }

      setIsSaving(true);
      try {
        if (currentAssembly && assemblyOption === 'global') {
          if (!isCreatingAssembly) {
            await updateGlobalAssembly(currentAssembly as GlobalAssembly.AsObject);
            setSaveStatus({
              open: true,
              message: 'Assembly updated successfully',
              severity: 'success',
            });
            handleSuccessfulSave();
          } else if (isCreatingAssembly) {
            const entityType: Foldertype = 'assembly';
            const newAssembly: GlobalAssembly.AsObject = {
              ...(currentAssembly as GlobalAssembly.AsObject),
              assemblymeasurementtype: currentAssembly.assemblymeasurementtype || 'count',
              isfile: true,
              imageclassificationid: currentAssembly.imageclassificationid || 1,
              entitytype: entityType,
              parentid: currentFolder?.folderid || '',
              level: currentFolder ? currentFolder.level + 1 : 0,
            };
            await createGlobalAssembly(newAssembly);
            setSaveStatus({
              open: true,
              message: 'Assembly created successfully',
              severity: 'success',
            });
            handleSuccessfulSave();
            handleSetViewingState({
              resetOption: 'resetAssembly',
              setStateFunctions: [
                {
                  setStateFunction: setIsCreatingAssembly,
                  prop: true,
                },
                {
                  setStateFunction: setAssemblyOption,
                  prop: 'global',
                },
                {
                  setStateFunction: setIsEditMode,
                  prop: false,
                },
              ],
            });
          }
        } else if (!isCreatingAssembly && assemblyOption === 'takeoff') {
          if (currentAssembly) {
            // This is the only place where breakouts should be updated (via CreateAssembly)
            // All other components should set shouldUpdateBreakout to false
            await submitAssembly({
              assembly: currentAssembly as TakeoffAssembly,
              shouldUpdateBreakout: true,
              isUpdating: true,
              isCreating: false,
              pageScale: userScale,
            });
            setSaveStatus({
              open: true,
              message: 'Takeoff assembly saved successfully',
              severity: 'success',
            });
            handleSuccessfulSave();
            handleSetViewingState({
              resetOption: 'resetAssembly',
              setStateFunctions: [
                {
                  setStateFunction: setIsViewingAssemblyForm,
                  prop: false,
                },
                {
                  setStateFunction: setIsEditMode,
                  prop: false,
                },
              ],
            });
          }
        }

        // If we're saving due to exiting, complete the exit action
        if (exitAction === 'close') {
          closeForm();
        } else if (exitAction === 'mode-switch') {
          performModeSwitch();
        }
        setExitAction(null);
      } catch (error) {
        console.error('Error creating or updating assembly:', error);
        setSaveStatus({
          open: true,
          message:
            'Error saving assembly: ' + (error instanceof Error ? error.message : 'Unknown error'),
          severity: 'error',
        });
      } finally {
        setIsSaving(false);
        setShouldSubmit(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      setAssemblyOption,
      setIsCreatingAssembly,
      setIsEditMode,
      setIsViewingAssemblyForm,
      userScale,
      setIsSaving,
      setShouldSubmit,
      setSaveStatus,
      currentFolder,
      exitAction,
      performModeSwitch,
      currentAssembly,
      assemblyOption,
      isCreatingAssembly,
      updateGlobalAssembly,
      createGlobalAssembly,
      submitAssembly,
      handleSuccessfulSave,
      handleSetViewingState,
      closeForm,
      shouldSubmit,
    ]
  );

  const handleClickSubmit = useCallback(() => {
    const now = Date.now();
    const DEBOUNCE_MS = 500; // 500ms debounce

    // Check if this is a rapid successive click
    if (now - lastSaveAttempt < DEBOUNCE_MS) {
      console.warn('Save request debounced - too rapid successive clicks');
      return;
    }

    setLastSaveAttempt(now);
    setShouldSubmit(true);
  }, [lastSaveAttempt]);

  const handleClose = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (hasUnsavedChanges) {
        setExitAction('close');
        setShowUnsavedDialog(true);
      } else {
        closeForm();
      }
    },
    [hasUnsavedChanges, setExitAction, setShowUnsavedDialog, closeForm]
  );

  const handleSaveAndClose = useCallback(async () => {
    setShowUnsavedDialog(false); // Close dialog first

    // Prevent concurrent save operations
    if (isSaving) {
      console.warn('Save already in progress, ignoring save and close request');
      return;
    }

    // Validate takeoff variables before saving
    if (currentAssembly && !validateAssemblyItems(currentAssembly)) {
      return;
    }

    // Check for exponential growth that might indicate duplication issues
    if (currentAssembly && detectExponentialGrowth(currentAssembly.itemsList, initialSnapshot)) {
      const confirmSave = window.confirm(
        'Warning: Unusual item growth detected in this assembly. This might indicate a duplication issue.\n\n' +
          'Do you want to proceed with saving and closing? If you suspect duplicate items, please review the assembly items before continuing.'
      );

      if (!confirmSave) {
        return;
      }
    }

    setIsSaving(true); // Show saving indicator

    try {
      // If in a global assembly
      if (currentAssembly && assemblyOption === 'global') {
        if (!isCreatingAssembly) {
          await updateGlobalAssembly(currentAssembly as GlobalAssembly.AsObject);
        } else if (isCreatingAssembly) {
          const entityType: Foldertype = 'assembly';
          const newAssembly: GlobalAssembly.AsObject = {
            ...(currentAssembly as GlobalAssembly.AsObject),
            assemblymeasurementtype: currentAssembly.assemblymeasurementtype || 'count',
            isfile: true,
            imageclassificationid: currentAssembly.imageclassificationid || 1,
            entitytype: entityType,
            parentid: currentFolder?.folderid || '',
            level: currentFolder ? currentFolder.level + 1 : 0,
          };
          await createGlobalAssembly(newAssembly);
        }
      }
      // If in a takeoff assembly
      else if (!isCreatingAssembly && assemblyOption === 'takeoff' && currentAssembly) {
        // This is the only place where breakouts should be updated (via CreateAssembly)
        // All other components should set shouldUpdateBreakout to false
        await submitAssembly({
          assembly: currentAssembly as TakeoffAssembly,
          shouldUpdateBreakout: true,
          isUpdating: true,
          isCreating: false,
          pageScale: userScale,
        });

        if (selectedEstimate) {
          EstimateAssemblyService.deleteExtendEstimate(currentAssembly?.assemblyid);
        } else if (selectedChangeOrder) {
          ChangeOrderAssemblyService.deleteExtendChangeOrder(currentAssembly?.assemblyid);
        }
      }

      // Handle successful save
      handleSuccessfulSave();
      setSaveStatus({
        open: true,
        message: 'Assembly saved successfully',
        severity: 'success',
      });

      // Complete the exit action
      if (exitAction === 'close') {
        closeForm();
      } else if (exitAction === 'mode-switch') {
        performModeSwitch();
      }
    } catch (error) {
      console.error('Error saving assembly:', error);
      setSaveStatus({
        open: true,
        message:
          'Error saving assembly: ' + (error instanceof Error ? error.message : 'Unknown error'),
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
      setExitAction(null);
      setHasUnsavedChanges(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentAssembly,
    assemblyOption,
    isCreatingAssembly,
    updateGlobalAssembly,
    createGlobalAssembly,
    submitAssembly,
    handleSuccessfulSave,
    closeForm,
    currentFolder,
    exitAction,
    performModeSwitch,
    setIsSaving,
    setSaveStatus,
    selectedEstimate,
    selectedChangeOrder,
    userScale,
  ]);

  const setIsHelpOpen = useStore(useUnityBuildStore).setIsHelpOpen;
  const isHelpOpen = useStore(useUnityBuildStore).isHelpOpen;

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: darkMode
            ? muiTheme.palette.background.default
            : muiTheme.palette.background.default,
          color: muiTheme.palette.text.primary,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative', // Ensure proper stacking context
        }}
      >
        <form
          onSubmit={handleSubmit}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          className="assemblyForm"
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {/* Input and Select Fields */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              flexShrink: 0,
              p: { xs: 1, sm: 2 },
              backgroundColor: darkMode
                ? alpha(muiTheme.palette.background.paper, 0.8)
                : muiTheme.palette.background.paper,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                gap: 1,
              }}
            >
              <TextField
                disabled={handleDisableRowActions()}
                id="assembly-name-field"
                label="Required Name"
                value={localAssemblyName}
                onChange={handleLocalNameChange}
                onBlur={handleNameBlur}
                sx={{
                  width: '100%',
                  margin: '10px',
                  '& .MuiInputBase-input': {
                    color: muiTheme.palette.text.primary,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiInputLabel-root': {
                    color: muiTheme.palette.text.secondary,
                  },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: darkMode
                      ? alpha(muiTheme.palette.background.paper, 0.2)
                      : alpha(muiTheme.palette.background.paper, 0.1),
                    '& fieldset': {
                      borderColor: alpha(muiTheme.palette.text.primary, 0.23),
                    },
                    '&:hover fieldset': {
                      borderColor: muiTheme.palette.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: muiTheme.palette.primary.main,
                    },
                  },
                }}
                required
                aria-required="true"
              />
              <FormControl
                style={{
                  margin: '10px',
                  width: '100%',
                }}
              >
                <InputLabel id="takeoff-type-label">Takeoff Type</InputLabel>
                <Select
                  disabled={handleDisableRowActions()}
                  labelId="takeoff-type-label"
                  id="takeoff-type-select"
                  label="Takeoff Type"
                  value={takeoffType}
                  onChange={handleMeasurementTypeChange}
                  sx={{
                    backgroundColor: darkMode
                      ? alpha(muiTheme.palette.background.paper, 0.2)
                      : alpha(muiTheme.palette.background.paper, 0.1),
                    color: muiTheme.palette.text.primary,
                    '& .MuiSelect-icon': {
                      color: muiTheme.palette.text.primary,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(muiTheme.palette.text.primary, 0.23),
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: muiTheme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: muiTheme.palette.primary.main,
                    },
                  }}
                >
                  <MenuItem value={'count'}>Count</MenuItem>
                  <MenuItem value={'length'}>Length</MenuItem>
                  <MenuItem value={'area'}>Area</MenuItem>
                </Select>
              </FormControl>
              {/*breakout selection*/}
              {currentAssembly && assemblyOption === 'takeoff' && (
                <Box sx={{ width: '100%' }}>
                  <BreakoutSelector
                    currentTakeoffAssembly={currentAssembly as TakeoffAssembly}
                    context="createAssembly"
                  />
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                mt: 0,
                width: 'auto',
              }}
            >
              <Button
                type="button"
                color={isCreatingAssembly ? 'primary' : isCreatingItem ? 'secondary' : 'error'}
                variant="contained"
                onClick={handleItemOrAssemblyMode}
                sx={{
                  margin: { xs: '2px', sm: '5px' },
                  width: '200px',
                  fontWeight: 500,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }}
              >
                {isCreatingAssembly
                  ? 'Enter Item Mode'
                  : isCreatingItem
                    ? 'Enter Assembly Mode'
                    : `Exit Edit Mode`}
              </Button>
              <Button
                type="submit"
                color="success"
                variant="contained"
                onClick={handleClickSubmit}
                disabled={isSaving || handleDisableRowActions()}
                sx={{
                  margin: { xs: '2px', sm: '5px' },
                  width: '200px',
                  fontWeight: 500,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }}
              >
                {isSaving ? 'Saving...' : 'Save Assembly'}
              </Button>

              <Button
                type="button"
                onClick={handleClose}
                color="error"
                variant="contained"
                sx={{
                  margin: { xs: '2px', sm: '5px' },
                  width: '200px',
                  fontWeight: 500,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }}
              >
                Close
              </Button>
              <Tooltip title="Click for help">
                <IconButton
                  onClick={() => setIsHelpOpen(!isHelpOpen)}
                  aria-label="Help information"
                  sx={{
                    color: darkMode
                      ? muiTheme.palette.primary.light
                      : muiTheme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: alpha(muiTheme.palette.primary.main, 0.1),
                    },
                    alignSelf: 'auto',
                  }}
                >
                  <HelpOutline />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flex: 1,
              bgcolor: darkMode
                ? alpha(muiTheme.palette.background.default, 0.9)
                : muiTheme.palette.background.default,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                flex: '0 0 50%',
                minWidth: '400px',
                height: '100%',
                overflow: 'hidden',
                borderRight: '1px solid #e0e0e0',
                backgroundColor: darkMode
                  ? alpha(muiTheme.palette.background.paper, 0.9)
                  : muiTheme.palette.background.paper,
              }}
            >
              <ItemTab />
            </Box>

            {/* Conditionally show CreateItemTab OR ItemQtyFormulas */}
            {isViewingItemForm ? (
              // If the user wants to create/edit an item, show <CreateItemTab> in the right pane
              <Box
                sx={{
                  flex: '1 1 auto',
                  minWidth: '400px',
                  height: '100%',
                  overflowY: 'auto',
                  backgroundColor: darkMode
                    ? alpha(muiTheme.palette.background.paper, 0.9)
                    : muiTheme.palette.background.paper,
                }}
              >
                <CreateItemTab />
              </Box>
            ) : (
              <Box
                sx={{
                  flex: '1 1 auto',
                  minWidth: '400px',
                  height: '100%',
                  overflowY: 'auto',
                  backgroundColor: darkMode
                    ? alpha(muiTheme.palette.background.paper, 0.9)
                    : muiTheme.palette.background.paper,
                }}
              >
                <TableContainer sx={{ maxHeight: '100%' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <FormulaHeader />
                    </thead>
                    <tbody>
                      {currentAssembly ? (
                        currentAssembly.itemsList?.length > 0 ? (
                          currentAssembly?.itemsList.map((item, index) => (
                            <AddedItemRow
                              key={`${item.itemid}-${index}`}
                              index={index}
                              item={item}
                            />
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6}>
                              <Typography
                                variant="body1"
                                sx={{
                                  p: 4,
                                  textAlign: 'center',
                                  color: muiTheme.palette.text.secondary,
                                }}
                              >
                                ⬅️ Navigate to the items tab to add items to your assembly.
                                <br />
                                ⬆️ Or press the "Enter Item Mode" button to create items.
                              </Typography>
                            </td>
                          </tr>
                        )
                      ) : currentAssembly ? (
                        <tr>
                          <td colSpan={6}>
                            <Typography
                              variant="body1"
                              sx={{
                                p: 4,
                                textAlign: 'center',
                                color: muiTheme.palette.text.secondary,
                              }}
                            >
                              No items in takeoff assembly
                            </Typography>
                          </td>
                        </tr>
                      ) : (
                        <tr>
                          <td colSpan={6}>
                            <Typography
                              variant="body1"
                              sx={{
                                p: 4,
                                textAlign: 'center',
                                color: muiTheme.palette.text.secondary,
                              }}
                            >
                              No assembly selected
                            </Typography>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </TableContainer>
              </Box>
            )}
          </Box>

          {/* Persistent unsaved changes indicator */}
          {hasUnsavedChanges && (
            <Box
              sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                zIndex: 1000,
              }}
            >
              <Alert
                severity="warning"
                variant="filled"
                sx={{
                  boxShadow: 3,
                  minWidth: '250px',
                  '& .MuiAlert-icon': {
                    color: 'inherit',
                  },
                }}
              >
                You have unsaved changes
              </Alert>
            </Box>
          )}

          {/* Unsaved Changes Dialog */}
          <Dialog
            open={showUnsavedDialog}
            onClose={() => setShowUnsavedDialog(false)}
            aria-labelledby="unsaved-changes-dialog"
            PaperProps={{
              sx: {
                backgroundColor: darkMode
                  ? alpha(muiTheme.palette.background.paper, 0.9)
                  : muiTheme.palette.background.paper,
                color: muiTheme.palette.text.primary,
              },
            }}
          >
            <DialogTitle
              sx={{
                borderBottom: `1px solid ${muiTheme.palette.divider}`,
                backgroundColor: darkMode ? '#2c2c2c' : '#f5f5f5',
              }}
            >
              Unsaved Changes
            </DialogTitle>
            <DialogContent
              sx={{
                py: 2,
                backgroundColor: darkMode
                  ? alpha(muiTheme.palette.background.paper, 0.9)
                  : muiTheme.palette.background.paper,
              }}
            >
              <Typography variant="body1">
                You have unsaved changes. Would you like to save before closing?
              </Typography>
            </DialogContent>
            <DialogActions
              sx={{
                p: 2,
                borderTop: `1px solid ${muiTheme.palette.divider}`,
                backgroundColor: darkMode ? '#2c2c2c' : '#f5f5f5',
              }}
            >
              <Button
                onClick={() => {
                  setHasUnsavedChanges(false);
                  setShowUnsavedDialog(false);
                  if (exitAction === 'close') {
                    closeForm();
                  } else if (exitAction === 'mode-switch') {
                    performModeSwitch();
                  }
                  setExitAction(null);
                }}
                color="error"
                variant="outlined"
                sx={{
                  color: muiTheme.palette.error.main,
                  borderColor: muiTheme.palette.error.main,
                  '&:hover': {
                    backgroundColor: alpha(muiTheme.palette.error.main, 0.1),
                    borderColor: muiTheme.palette.error.dark,
                  },
                }}
              >
                Discard Changes
              </Button>
              <Button
                onClick={() => {
                  setShowUnsavedDialog(false);
                  setExitAction(null);
                }}
                color="primary"
                variant="outlined"
                sx={{
                  color: darkMode ? muiTheme.palette.primary.light : muiTheme.palette.primary.main,
                  borderColor: darkMode
                    ? muiTheme.palette.primary.light
                    : muiTheme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(muiTheme.palette.primary.main, 0.1),
                    borderColor: darkMode
                      ? muiTheme.palette.primary.light
                      : muiTheme.palette.primary.dark,
                  },
                }}
              >
                Continue Editing
              </Button>
              <Button
                onClick={() => {
                  handleSaveAndClose();
                }}
                color="success"
                variant="contained"
                autoFocus
                sx={{
                  backgroundColor: muiTheme.palette.success.main,
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: muiTheme.palette.success.dark,
                  },
                }}
              >
                Save & Close
              </Button>
            </DialogActions>
          </Dialog>
        </form>

        {/* Loading Dialog */}
        <Dialog
          open={isSaving}
          PaperProps={{
            sx: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              overflow: 'hidden',
            },
          }}
          aria-labelledby="saving-dialog"
        >
          <DialogContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <CircularProgress
              sx={{
                color: darkMode ? muiTheme.palette.primary.light : muiTheme.palette.primary.main,
              }}
            />
            <Typography sx={{ color: 'white', mt: 2, fontWeight: 500 }}>
              Saving Assembly...
            </Typography>
          </DialogContent>
        </Dialog>

        {/* Status Snackbar */}
        <Snackbar
          open={saveStatus.open}
          autoHideDuration={6000}
          onClose={() => setSaveStatus(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSaveStatus(prev => ({ ...prev, open: false }))}
            severity={saveStatus.severity}
            variant="filled"
            sx={{
              '& .MuiAlert-icon': {
                color: 'inherit',
              },
            }}
          >
            {saveStatus.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
}

export default React.memo(CreateAssembly);
